class TabsComponent extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.tabNav = this.querySelector('[data-vf-tabs-nav]');
    /** @type {HTMLElement | null} */
    this.tabPanelsContainer = this.querySelector('[data-vf-tabs-panels-container]');
    /** @type {NodeListOf<HTMLElement>} */
    this.tabNavItems = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('[data-vf-tabs-nav-item]')
    );
    /** @type {NodeListOf<HTMLElement>} */
    this.tabPanels = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('[data-vf-tabs-panel]')
    );
    /** @type {((event: MediaQueryListEvent) => void) | null} */
    this.motionMediaQueryHandler = null;
    /** @type {Promise<void>} */
    this.currentAnimation = Promise.resolve();
    this.prefersReducedMotion = this.getPrefersReducedMotion();
    /** @type {MediaQueryList | null} */
    this.motionMediaQuery = this.setupMotionListener();

    this.tabGroupId = `tabs-block-${this.id}`;
    
    this.init();
  }

  init() {
    // Set up initial ARIA attributes and IDs
    this.tabNav?.setAttribute('role', 'tablist');

    const defaultActiveIndex = this.getInitialActiveIndex();
    
    this.tabNavItems.forEach((tab, index) => {
      // Create unique IDs for each tab and panel
      const tabId = `${this.tabGroupId}-tab-${index}`;
      const panelId = `${this.tabGroupId}-panel-${index}`;
      
      // Set up tab attributes
      tab.setAttribute('role', 'tab');
      tab.setAttribute('id', tabId);
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('tabindex', index === defaultActiveIndex ? '0' : '-1');
      tab.setAttribute('aria-selected', index === defaultActiveIndex ? 'true' : 'false');
      
      // Set up panel attributes
      const panel = this.tabPanels[index];
      if (panel) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('id', panelId);
        panel.setAttribute('aria-labelledby', tabId);
        panel.setAttribute('tabindex', '0');
        panel.hidden = index !== defaultActiveIndex;
        panel.setAttribute('aria-hidden', index === defaultActiveIndex ? 'false' : 'true');
        panel.classList.toggle('is-active', index === defaultActiveIndex);
      }
      
      tab.addEventListener('click', this.onTabClick.bind(this));
      tab.addEventListener('keydown', this.onKeyDown.bind(this));
    });
  }

  /**
   * @returns {number}
   */
  getInitialActiveIndex() {
    const desiredLabel =
      (this.dataset.initialTab ? this.dataset.initialTab.trim().toLowerCase() : '') ?? '';

    if (!desiredLabel) {
      return 0;
    }

    const tabArray = Array.from(this.tabNavItems);
    const matchingIndex = tabArray.findIndex((tab) => {
      const label = tab.dataset.tabLabel ?? tab.textContent ?? '';
      return label.trim().toLowerCase() === desiredLabel;
    });

    return matchingIndex >= 0 ? matchingIndex : 0;
  }

  /**
   * @returns {MediaQueryList | null}
   */
  setupMotionListener() {
    if (!window.matchMedia) return null;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.prefersReducedMotion = mediaQuery.matches;
    /** @param {MediaQueryListEvent} event */
    const handler = (event) => {
      this.prefersReducedMotion = event.matches;
    };
    this.motionMediaQueryHandler = handler;

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handler);
    }

    return mediaQuery;
  }

  /**
   * @returns {boolean}
   */
  getPrefersReducedMotion() {
    if (!window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Clean up media query listeners when the component is removed.
   */
  disconnectedCallback() {
    if (!this.motionMediaQuery || !this.motionMediaQueryHandler) return;

    if (this.motionMediaQuery.removeEventListener) {
      this.motionMediaQuery.removeEventListener('change', this.motionMediaQueryHandler);
    } else if (this.motionMediaQuery.removeListener) {
      this.motionMediaQuery.removeListener(this.motionMediaQueryHandler);
    }

    this.motionMediaQueryHandler = null;
    this.motionMediaQuery = null;
  }

  /**
   * @param {MouseEvent} event
   */
  onTabClick(event) {
    const clickedTab = event.currentTarget;
    if (!(clickedTab instanceof HTMLElement)) {
      return;
    }
    this.switchTab(clickedTab);
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKeyDown(event) {
    const targetTab = event.currentTarget;
    if (!(targetTab instanceof HTMLElement)) {
      return;
    }

    const tabArray = Array.from(this.tabNavItems);
    const index = tabArray.indexOf(targetTab);
    
    /** @type {HTMLElement | null} */
    let newTab = null;
    
    switch (event.key) {
      case 'ArrowLeft': {
        const candidate =
          tabArray[index - 1] ?? tabArray[tabArray.length - 1] ?? null;
        newTab = candidate;
        break;
      }
      case 'ArrowRight': {
        const candidate = tabArray[index + 1] ?? tabArray[0] ?? null;
        newTab = candidate;
        break;
      }
      case 'Home': {
        newTab = tabArray[0] ?? null;
        break;
      }
      case 'End': {
        newTab = tabArray[tabArray.length - 1] ?? null;
        break;
      }
      default:
        return;
    }
    
    if (!newTab) {
      return;
    }

    event.preventDefault();
    this.switchTab(newTab);
  }

  /**
   * @param {HTMLElement} newTab
   */
  switchTab(newTab) {
    if (!(newTab instanceof HTMLElement)) {
      return;
    }

    // Get the old tab
    const oldTab = this.querySelector('[aria-selected="true"]');
    if (!(oldTab instanceof HTMLElement)) {
      return;
    }

    if (oldTab === newTab) return;

    // Switch the tabs
    oldTab.setAttribute('aria-selected', 'false');
    oldTab.setAttribute('tabindex', '-1');
    newTab.setAttribute('aria-selected', 'true');
    newTab.setAttribute('tabindex', '0');
    newTab.focus();

    // Switch the panels
    const newPanelId = newTab.getAttribute('aria-controls');
    if (!newPanelId) {
      return;
    }

    const newPanel = this.querySelector(`#${newPanelId}`);
    const oldPanelId = oldTab.getAttribute('aria-controls');
    const oldPanel = oldPanelId ? this.querySelector(`#${oldPanelId}`) : null;

    if (this.tabPanelsContainer && oldPanel instanceof HTMLElement) {
      this.tabPanelsContainer.style.minHeight = `${oldPanel.offsetHeight}px`;
    }

    const animation = this.hidePanel(oldPanel).then(() => this.showPanel(newPanel));

    this.currentAnimation = animation;

    animation.finally(() => {
      if (this.currentAnimation !== animation) return;
      if (this.tabPanelsContainer) {
        this.tabPanelsContainer.style.minHeight = '';
      }
    });
  }

  /**
   * @param {HTMLElement | Element | null} panel
   * @returns {Promise<void>}
   */
  hidePanel(panel) {
    if (!(panel instanceof HTMLElement) || panel.hidden) {
      return Promise.resolve();
    }

    panel.classList.remove('is-active');
    panel.setAttribute('aria-hidden', 'true');

    return this.waitForTransition(panel).then(() => {
      panel.hidden = true;
    });
  }

  /**
   * @param {HTMLElement | Element | null} panel
   * @returns {Promise<void>}
   */
  showPanel(panel) {
    if (!(panel instanceof HTMLElement)) {
      return Promise.resolve();
    }

    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    // Force reflow so the transition triggers reliably
    void panel.getBoundingClientRect();
    panel.classList.add('is-active');

    return this.waitForTransition(panel);
  }

  /**
   * @param {HTMLElement | Element | null} element
   * @returns {Promise<void>}
   */
  waitForTransition(element) {
    if (this.prefersReducedMotion || !(element instanceof HTMLElement)) {
      return Promise.resolve();
    }

    const style = window.getComputedStyle(element);
    const maxDuration = this.getMaxTransitionDuration(style);

    if (!maxDuration) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let resolved = false;
      /**
       * @param {boolean} eventListenerAttached
       * @param {number | undefined} timeoutId
       */
      const cleanup = (eventListenerAttached, timeoutId) => {
        if (resolved) return;
        resolved = true;
        if (eventListenerAttached) {
          element.removeEventListener('transitionend', onTransitionEnd);
        }
        if (timeoutId !== undefined) {
          window.clearTimeout(timeoutId);
        }
        resolve();
      };

      /** @param {TransitionEvent} event */
      const onTransitionEnd = (event) => {
        if (event.target !== element || event.propertyName !== 'opacity') return;
        cleanup(true, timeoutId);
      };

      element.addEventListener('transitionend', onTransitionEnd);

      const timeoutId = window.setTimeout(() => {
        cleanup(true, timeoutId);
      }, maxDuration + 50);
    });
  }

  /**
   * @param {CSSStyleDeclaration} style
   * @returns {number}
   */
  getMaxTransitionDuration(style) {
    const durations = this.parseTimeValues(style.transitionDuration);
    const delays = this.parseTimeValues(style.transitionDelay);
    const count = Math.max(durations.length, delays.length);

    if (!count) return 0;

    let max = 0;

    for (let index = 0; index < count; index += 1) {
      const duration = durations[index] ?? durations[durations.length - 1] ?? 0;
      const delay = delays[index] ?? delays[delays.length - 1] ?? 0;
      max = Math.max(max, duration + delay);
    }

    return max;
  }

  /**
   * @param {string} value
   * @returns {number[]}
   */
  parseTimeValues(value) {
    if (!value) return [];
    return value
      .split(',')
      .map(
        /**
         * @param {string} item
         * @returns {string}
         */
        (item) => item.trim(),
      )
      .map(
        /**
         * @param {string} item
         * @returns {number}
         */
        (item) => {
        if (!item) return 0;
        if (item.endsWith('ms')) {
          return parseFloat(item);
        }
        if (item.endsWith('s')) {
          return parseFloat(item) * 1000;
        }
        const numeric = parseFloat(item);
        return Number.isNaN(numeric) ? 0 : numeric * 1000;
        },
      );
  }
}

customElements.define('tabs-component', TabsComponent); 